import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, X, Info } from "lucide-react";
import Modal from "components/common/Modal";

const AddWorkspaceModal = ({ show, onClose, onCreate }) => {
  const { t } = useTranslation();
  const [workspaceName, setWorkspaceName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setWorkspaceName("");
    onClose();
  };

  const handleCreate = async () => {
    if (!workspaceName.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onCreate?.({ name: workspaceName });
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = workspaceName.trim().length > 0;

  return (
    <Modal show={show} onClose={handleClose} maxWidth="480px">
      {/* Header */}
      <div
        className="relative overflow-hidden p-7"
        style={{
          background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
        }}
      >
        {/* Decorative circles */}
        <div
          className="absolute rounded-full"
          style={{
            width: 120,
            height: 120,
            top: -30,
            right: -30,
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 80,
            height: 80,
            bottom: -40,
            right: 60,
            background: "rgba(255, 255, 255, 0.08)",
          }}
        />

        {/* Header content */}
        <div className="relative flex items-start justify-between">
          <div>
            {/* Icon */}
            <div
              className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <Building2 size={24} className="text-white" />
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold text-white">
              {t("workspace.addWorkspaceModal.title")}
            </h2>
            <p
              className="mt-1.5 text-sm"
              style={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              {t("workspace.addWorkspaceModal.subtitle")}
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={handleClose}
            className="flex h-9 w-9 items-center justify-center rounded-[10px] transition-colors duration-150"
            style={{ background: "rgba(255, 255, 255, 0.2)" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)")
            }
          >
            <X size={18} className="text-white" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-7">
        {/* Workspace Name Field */}
        <div className="mb-7">
          <label className="mb-2 block text-sm font-medium text-[#374151]">
            {t("workspace.addWorkspaceModal.nameLabel")}
          </label>
          <input
            type="text"
            value={workspaceName}
            onChange={(e) => setWorkspaceName(e.target.value)}
            placeholder={t("workspace.addWorkspaceModal.namePlaceholder")}
            className="w-full rounded-[10px] border border-[#E5E7EB] px-4 py-3 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150"
            style={{
              boxShadow: "none",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#E07A3A";
              e.target.style.boxShadow = "0 0 0 3px rgba(224, 122, 58, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#E5E7EB";
              e.target.style.boxShadow = "none";
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && isValid) {
                handleCreate();
              }
            }}
          />
        </div>

        {/* Info Box */}
        <div
          className="flex gap-3 rounded-xl p-4"
          style={{
            background:
              "linear-gradient(135deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 100%)",
            border: "1px solid rgba(232, 133, 74, 0.15)",
          }}
        >
          <Info size={20} className="mt-0.5 flex-shrink-0 text-[#E07A3A]" />
          <p className="text-[13px] leading-relaxed text-[#6B7280]">
            {t("workspace.addWorkspaceModal.info")}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 border-t border-[#F3F4F6] px-7 py-5">
        <button
          onClick={handleClose}
          className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition-colors duration-150 hover:bg-[#F9FAFB]"
        >
          {t("common.actions.cancel")}
        </button>
        <button
          onClick={handleCreate}
          disabled={!isValid || isSubmitting}
          className="rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-150"
          style={
            isValid && !isSubmitting
              ? {
                  background:
                    "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                  boxShadow: "0 2px 8px rgba(224, 122, 58, 0.3)",
                  cursor: "pointer",
                }
              : {
                  background: "#F3F4F6",
                  color: "#9CA3AF",
                  cursor: "not-allowed",
                }
          }
        >
          {isSubmitting
            ? t("workspace.shared.creating")
            : t("workspace.addWorkspaceModal.submit")}
        </button>
      </div>
    </Modal>
  );
};

export default AddWorkspaceModal;
